import type { GraphDatabase } from "@ctrl/controller-rpc";

import { getBuiltinKind } from "@/components/databases";
import {
  introspectType,
  TypePropertyInfo,
} from "@/components/graph/introspection";
import type { DefaultConsoleKind } from "@/components/parsed-input";

// The value types themselves live in parsed-input.ts (they are part of
// `CreateObjectQuery`, which the Electron main process type-checks without
// `.vue` support); this module hosts everything else about the default types'
// console pills and re-exports the types as the single console-defaults
// entry point for UI code.
export type {
  ConsoleDefaultValues,
  DefaultConsoleKind,
} from "@/components/parsed-input";

export const defaultConsoleKindOf = (
  database: GraphDatabase
): DefaultConsoleKind | undefined => {
  const kind = getBuiltinKind(database);
  if (kind === "task" || kind === "event" || kind === "page") return kind;
  return undefined;
};

/**
 * User-facing names of the default types (their extensions databases stand in
 * for the types themselves, so these win over the database names).
 */
export const DEFAULT_TYPE_LABELS: Record<DefaultConsoleKind, string> = {
  task: "Task",
  event: "Event",
  page: "Page",
};

/**
 * Pill ids for the default types' core (non-extension) fields. Extension-backed
 * pills (Labels, Priority) keep their real column ids from the extensions
 * database; these synthetic ids are namespaced so they can never collide.
 */
export const DEFAULT_PILL = {
  plannedFor: "console-default:planned-for",
  starred: "console-default:starred",
  notes: "console-default:notes",
  date: "console-default:date",
  duration: "console-default:duration",
  allDay: "console-default:all-day",
  calendar: "console-default:calendar",
  active: "console-default:active",
  busy: "console-default:busy",
  location: "console-default:location",
  description: "console-default:description",
  participants: "console-default:participants",
} as const;

/**
 * Duration of console-created events when the Duration pill is left unset —
 * the value the pill displays by default (see `eventTimeFromDefaults` and the
 * event fallback time in `console.ts`).
 */
export const DEFAULT_EVENT_DURATION_MINUTES = 60;

const synthetic = (
  id: string,
  name: string,
  type: TypePropertyInfo["type"],
  extra: Partial<TypePropertyInfo> = {}
): TypePropertyInfo => ({
  id,
  name,
  type,
  optional: true,
  list: false,
  title: false,
  virtual: false,
  ...extra,
});

/**
 * The extension-backed pill properties (Labels, Priority, custom user
 * columns, ...) for a default type, introspected from its extensions database
 * — the same way custom types surface their properties.
 */
const extensionPillProperties = (
  extensionsDb: GraphDatabase | null
): TypePropertyInfo[] => {
  if (!extensionsDb) return [];
  // The title column is the object's own title, captured by the console input.
  // Virtual fields are the controller's read-only mirrors of core object
  // fields (PlannedFor, Starred, Notes, Status, ClosedAt, ...): they cannot be
  // set at creation and would duplicate the synthetic pills below.
  return introspectType(extensionsDb).filter(
    (property) => !property.title && !property.virtual
  );
};

/**
 * The property pills shown for a default type in the console. Core object
 * fields (scheduled date, starred, notes, event time, ...) are synthesized
 * here; Labels/Priority come from the type's extensions database when present.
 * `calendars` fills the event Calendar pill's choices (calendar summaries).
 */
export const defaultTypePillProperties = (
  kind: DefaultConsoleKind,
  extensionsDb: GraphDatabase | null,
  calendars: string[] = []
): TypePropertyInfo[] => {
  const extensions = extensionPillProperties(extensionsDb);
  switch (kind) {
    case "task":
      return [
        synthetic(DEFAULT_PILL.plannedFor, "Planned for", "Date"),
        synthetic(DEFAULT_PILL.notes, "Notes", "Notes"),
        // No Allocations pill: virtual on the extensions database, so already
        // excluded by `extensionPillProperties`.
        ...extensions,
        synthetic(DEFAULT_PILL.starred, "Starred", "Boolean"),
      ];
    case "event":
      return [
        synthetic(DEFAULT_PILL.date, "Date", "Date"),
        synthetic(DEFAULT_PILL.duration, "Duration", "Number", {
          numberType: "int",
        }),
        synthetic(DEFAULT_PILL.allDay, "All day", "Boolean"),
        synthetic(DEFAULT_PILL.calendar, "Calendar", "Select", {
          values: calendars.map((value) => ({ value })),
        }),
        synthetic(DEFAULT_PILL.active, "Active", "Boolean"),
        synthetic(DEFAULT_PILL.busy, "Busy", "Boolean"),
        synthetic(DEFAULT_PILL.description, "Description", "Notes"),
        synthetic(DEFAULT_PILL.notes, "Notes", "Notes"),
        ...extensions,
        // No Participants pill
        // No Location pill
      ];
    case "page":
      return [...extensions, synthetic(DEFAULT_PILL.notes, "Notes", "Notes")];
  }
};
